import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';
import type { DatovkaApiType, OsobyFyzickaOsobaDto } from '@gov-nx/api';
import {
	sendSubmissionQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { useIdentity } from '@gov-nx/auth/common';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useMessageEvents } from '@gov-nx/core/events';
import type {
	FormWizardHook,
	PersonCommunicationData,
} from '@gov-nx/core/hooks';
import {
	useCountryCodes,
	usePersonCommunicationData,
	usePersonCommunicationForm,
	useRobPersonQuery,
	useWizard,
} from '@gov-nx/core/hooks';
import { getRequiredDataBoxes } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { useDataBoxAccess } from '@gov-nx/module/data-box';
import type { IUseBoolean } from '@gov-nx/utils/common';
import {
	dataBoxParams,
	delay,
	phoneShapeToString,
	useBoolean,
} from '@gov-nx/utils/common';
import { ServiceCode } from '../../definitions/codes';
import { useElections } from '../useElections';
import { useKorespondencniVolbyLocale } from '../useKorespondencniVolbyLocale';
import type {
	KorespondencniHlasovaniZapisFormStep1,
	KorespondencniHlasovaniZapisStep1,
} from './useFormStep1';
import { useFormStep1 } from './useFormStep1';
import type {
	KorespondencniHlasovaniZapisFormStep2,
	KorespondencniHlasovaniZapisStep2,
} from './useFormStep2';
import { useFormStep2 } from './useFormStep2';
import type {
	KorespondencniHlasovaniZapisFormStep3,
	KorespondencniHlasovaniZapisStep3,
} from './useFormStep3';
import { useFormStep3 } from './useFormStep3';

interface KorespondencniHlasovaniZapisContext {
	wizard: FormWizardHook<ZapisForm>;
	step1: KorespondencniHlasovaniZapisFormStep1;
	step2: KorespondencniHlasovaniZapisFormStep2;
	step3: KorespondencniHlasovaniZapisFormStep3;
	personCommunicationData: PersonCommunicationData;
	isLoading: boolean;
	isSubmitting: boolean;
	isSubmittingWithRedirection: boolean;
	isSubmittingWithoutRedirection: boolean;
	isSubmittingWithoutModal: boolean;
	person?: OsobyFyzickaOsobaDto;
	canBeRedirectedToElectionDocuments: boolean;
	isSubmitModalOpen: IUseBoolean;
	showConnectDataboxBanner: boolean;
	databoxTypes: DatovkaApiType['typSchranky'][];
	isCzechCitizen: boolean;
	recapKeyValues: { label: string; value: string }[];
	submit: (redirectToElection?: boolean) => void;
}

type ZapisForm = KorespondencniHlasovaniZapisStep1 &
	KorespondencniHlasovaniZapisStep2 &
	KorespondencniHlasovaniZapisStep3;

const ServiceContext =
	createContext<Nullable<KorespondencniHlasovaniZapisContext>>(null);

interface Props {
	onSubmitSuccess: (redirectToElectionId?: number) => void;
	children: ReactNode;
}

export const KorespondencniHlasovaniZapisContextProvider = ({
	children,
	onSubmitSuccess,
}: Props) => {
	const {
		extraConfig: { application },
	} = useExtraConfigContext();
	const databoxTypes = getRequiredDataBoxes(
		ServiceCode['zadost-o-korespondencni-hlasovani'],
		application
	);
	const personQuery = useRobPersonQuery();
	const countryCodes = useCountryCodes();
	const { t } = useKorespondencniVolbyLocale();
	const { toastMessageSuccess } = useMessageEvents();
	const { isDataBoxLoginType } = useIdentity();
	const { areAnyConnected } = useDataBoxAccess({
		requiredTypes: databoxTypes,
	});

	const { isCzechCitizen } = useRobPersonQuery();
	const isSubmitModalOpen = useBoolean();

	// Issue with delay after submit
	// TODO: use  const isSubmitting = submitMutation.isLoading;
	const isSubmitting = useBoolean();
	const { axiosInstance } = useAxiosInstance();
	const submitMutation = usePoMutation({
		mutationFn: async (
			variables: ZapisForm & { redirectToElectionId?: number }
		) => {
			isSubmitting.setTrue();
			const { datovaSchrankaId, ...params } = dataBoxParams(variables);
			return sendSubmissionQuery(axiosInstance)({
				params: {
					odesilatelDSId: datovaSchrankaId,
					...params,
				},
				body: {
					kodPodani: 'ZADOST_ZAPIS_ZVLASTNI_SEZNAM_VOLICU',
					zuKod: variables.officeId,
					adresaUlice: variables.street,
					adresaMesto: variables.city,
					adresaPsc: variables.zip,
					adresaStat: variables.country,
					email: variables._useEmail ? variables.email : undefined,
					telefon:
						variables._usePhone && variables.phone
							? phoneShapeToString(variables.phone)
							: undefined,
				},
			});
		},
		onSuccess: async (data, variables) => {
			toastMessageSuccess(t('zapis.zpravy.odeslano'));
			wizard.resetForms();

			isSubmitModalOpen.setFalse();
			// kdyz se zavre modal a nasledny redirect se provede moc brzo, tak asi jeste nekde visi nejaka class ktera pak znemoznuje scrolovat, chvilku pockat to resi krasne
			await delay(10);

			isSubmitting.setFalse();

			onSubmitSuccess(variables.redirectToElectionId);
		},
		onError: () => {
			isSubmitModalOpen.setFalse();
			isSubmitting.setFalse();
		},
	});

	const step1 = useFormStep1({ isDisabled: isSubmitting.isTrue });
	const step2 = useFormStep2({ isDisabled: isSubmitting.isTrue });
	const step3 = useFormStep3({
		isDisabled: isSubmitting.isTrue,
		databoxTypes,
	});

	const personCommunicationData = usePersonCommunicationData();
	usePersonCommunicationForm(step1, {
		countryCodes,
		email: personCommunicationData.emailVerified,
		phone: personCommunicationData.phoneVerified,
	});

	const wizard = useWizard<ZapisForm>({
		steps: [step1, step2.form, step3],
		onSubmit: async (values) => submitMutation.mutate(values),
		onStepInvalid: (index, errors) => {
			if (index === 0) {
				personCommunicationData.onFormValidated(errors);
			}
		},
	});

	const elections = useElections();

	const recapKeyValues = [
		{
			label: t('zapis.rekapitulace.udaje-pro-zapis.zastupitelsky-urad'),
			value: `${step2.selectedCountry}, ${step2.selectedDistrict}, ${step2.selectedOffice}`,
		},
		{
			label: t('zapis.rekapitulace.udaje-pro-zapis.adresa-pobytu'),
			value: `${step2.form.formMethods.getValues(
				'street'
			)}, ${step2.form.formMethods.getValues(
				'city'
			)} ${step2.form.formMethods.getValues(
				'zip'
			)}, ${step2.form.formMethods.getValues('country')}`,
		},
	];

	const submit = (redirectToElection?: boolean) => {
		submitMutation.mutate({
			...wizard.formData,
			redirectToElectionId: redirectToElection
				? elections.elections[0].volbyId
				: undefined,
		});
	};

	const canBeRedirectedToElectionDocuments =
		elections.canVote && elections.elections.length === 1;

	return (
		<ServiceContext.Provider
			value={{
				isLoading:
					personQuery.isLoading ||
					step2.isLoading ||
					countryCodes.isLoading ||
					elections.isLoading,
				wizard,
				personCommunicationData,
				step1,
				step2: step2.form,
				step3,
				isSubmitting: isSubmitting.isTrue,
				isSubmittingWithRedirection:
					isSubmitting.isTrue &&
					!!submitMutation.variables?.redirectToElectionId,
				isSubmittingWithoutRedirection:
					isSubmitting.isTrue &&
					!submitMutation.variables?.redirectToElectionId,
				isSubmittingWithoutModal:
					isSubmitting.isTrue && isSubmitModalOpen.isFalse,
				person: personQuery.data,
				canBeRedirectedToElectionDocuments,
				isSubmitModalOpen,
				showConnectDataboxBanner: isDataBoxLoginType && !areAnyConnected,
				databoxTypes,
				isCzechCitizen,
				recapKeyValues,
				submit,
			}}>
			{children}
		</ServiceContext.Provider>
	);
};

export const useKorespondencniHlasovaniZapisContext = () => {
	const context = useContext(ServiceContext);
	if (!context) {
		throw new Error('Context must be used within a ContextProvider');
	}
	return context;
};
